import React, { createContext, useCallback, useContext, useMemo, useState } from 'react'

import { AIChatCitationFragment } from 'types/graphql'

import useDisclosure, { UseDisclosureReturn } from 'src/hooks/use-disclosure'

// Message type for Staff AI Chat
export type StaffAIChatMessage = {
  id: string | number
  text: string
  sender: 'user' | 'ai'
  questionCreatedAt?: string
  responseCreatedAt?: string
  isThinking?: boolean
  citations?: AIChatCitationFragment[]
}

// Context value type for the Staff AI Assistant
type StaffAIAssistantContextValue = {
  // Overlay state
  overlay: UseDisclosureReturn
  // Message state - persists across overlay open/close within same React session
  messages: StaffAIChatMessage[]
  setMessages: React.Dispatch<React.SetStateAction<StaffAIChatMessage[]>>
  // Typing indicator state
  isAssistantTyping: boolean
  setIsAssistantTyping: React.Dispatch<React.SetStateAction<boolean>>
  // Start a new chat (clears messages)
  startNewChat: () => void
}

// Context for controlling the Staff AI Assistant overlay and persisting conversation
// Using React Context because:
// - We plan to open this from multiple places (Navbar, keyboard shortcut, etc.)
// - Messages should persist across overlay open/close within the same browser session
// - Users can start a fresh chat when they want via "New Chat" button
const StaffAIAssistantContext = createContext<StaffAIAssistantContextValue | null>(null)

// Returns null if used outside the provider - callers should handle the null case
export const useStaffAIAssistant = (): StaffAIAssistantContextValue | null => {
  return useContext(StaffAIAssistantContext)
}

type Props = {
  children: React.ReactNode
}

export const StaffAIAssistantProvider = ({ children }: Props) => {
  const overlay = useDisclosure()
  const [messages, setMessages] = useState<StaffAIChatMessage[]>([])
  const [isAssistantTyping, setIsAssistantTyping] = useState(false)

  const startNewChat = useCallback(() => {
    setMessages([])
    setIsAssistantTyping(false)
  }, [])

  const value = useMemo<StaffAIAssistantContextValue>(
    () => ({
      overlay,
      messages,
      setMessages,
      isAssistantTyping,
      setIsAssistantTyping,
      startNewChat,
    }),
    [overlay, messages, isAssistantTyping, startNewChat]
  )

  return (
    <StaffAIAssistantContext.Provider value={value}>{children}</StaffAIAssistantContext.Provider>
  )
}
